import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Shield, Clock, FileText, Sparkles } from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';

export function PaymentConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { upgradeToPremium, tier } = useSubscription();
  const [progress, setProgress] = useState(0);
  const [upgradeComplete, setUpgradeComplete] = useState(false);
  const swishNumber = location.state?.swishNumber;

  useEffect(() => {
    if (!swishNumber) {
      navigate('/');
      return;
    }

    // Simulate payment processing
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(timer);
          // When payment is complete, upgrade to premium
          if (!upgradeComplete) {
            upgradeToPremium();
            setUpgradeComplete(true);
          }
          return 100;
        }
        return prev + 20;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [swishNumber, navigate, upgradeToPremium, upgradeComplete]);

  const steps = [
    { title: "Betalning initierad", done: progress >= 20 },
    { title: "Verifierar Swish-nummer", done: progress >= 40 },
    { title: "Förbereder AI-system", done: progress >= 60 },
    { title: "Aktiverar premium-funktioner", done: progress >= 80 },
    { title: "Slutför processen", done: progress >= 100 }
  ];

  const features = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "AI-Driven Ansökningshjälp",
      description: "Din personliga AI-assistent är redo att hjälpa dig"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Premium Support",
      description: "Direktsupport från våra experter"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Snabb Process",
      description: "Optimerad ansökningsprocess på 15 minuter"
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Exklusiva Mallar",
      description: "Tillgång till professionella ansökningsmallar"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {progress >= 100 ? 'Premium aktiverat!' : 'Aktiverar din premium-tjänst'}
          </h1>
          <p className="text-gray-600">
            {progress >= 100 
              ? 'Alla premium-funktioner är nu tillgängliga i Verktyget'
              : 'Vi förbereder dina verktyg och aktiverar alla premium-funktioner'}
          </p>
          {progress >= 100 && (
            <div className="mt-3 inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
              Prenumerationsstatus: {tier === 'premium' ? 'Premium' : 'Gratis'}
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="space-y-6">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center
                              ${step.done ? 'bg-green-100' : 'bg-gray-100'}`}>
                  {step.done ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <div className="w-3 h-3 rounded-full bg-gray-300" />
                  )}
                </div>
                <div className="flex-grow">
                  <p className={`font-medium ${step.done ? 'text-green-600' : 'text-gray-600'}`}>
                    {step.title}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`bg-white p-6 rounded-xl shadow-md transform transition-all duration-500
                       ${progress >= 100 ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-2 bg-rose-100 rounded-lg">
                  <div className="text-rose-600">
                    {feature.icon}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {progress >= 100 && (
          <div className="text-center animate-fadeIn">
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center px-6 py-3 bg-rose-600 text-white 
                       rounded-lg hover:bg-rose-700 transition-colors font-semibold
                       shadow-lg hover:shadow-xl"
            >
              Börja använda premium-funktioner
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
